import { useQuery } from "@apollo/client";
import { Button, Stack } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import Error from "../common/Error";
import Loading from "../common/Loading";
import { useLogging } from "../logging/useLogging";
import { pages } from "../pages/pages";
import { SettingsLocationState } from "../types";
import { AppEvent } from "../types";
import { isTraditionalExerciseId } from "../utils/exercises";
import { getSettings } from "../utils/localStorage";
import { calculateTimePassedSeconds } from "../utils/timeHelpers";
import { useBlocker } from "../utils/useBlocker";
import { useFavouritesAndExcluded } from "../utils/useFavouritesAndExcluded";
import { getExercisesQuery, GetExercisesQueryType, GetExercisesQueryVariables } from "./Exercise.gql";
import ExerciseContent from "./ExerciseContent";
import ExerciseTemplate from "./ExerciseTemplate";
import { useExerciseSessionWrapper } from "./utils/useExerciseSessionWrapper";

const TraditionalExercise: React.FunctionComponent = () => {
    const location = useLocation();
    const { settings } = (location.state as SettingsLocationState | undefined) ?? { settings: getSettings() };
    const [startTime, setStartTime] = React.useState<Date>(new Date());
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [currentExerciseIndex, setCurrentExerciseIndex] = React.useState(0);

    const { addExercise, finishSessionAndNavigateToSummary } = useExerciseSessionWrapper();

    const { favourites, excluded } = useFavouritesAndExcluded();

    const { logWithPayload } = useLogging();

    const { data, loading } = useQuery<GetExercisesQueryType, GetExercisesQueryVariables>(getExercisesQuery, {
        variables: {
            discreet: settings.discreet === "yes" ? true : false,
            categories: settings.categories,
            ids: settings.favourite === "yes" ? favourites.filter(isTraditionalExerciseId) : undefined,
            excludedIds: excluded.filter(isTraditionalExerciseId),
        },
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            if (data.exercises.length != 0) {
                logWithPayload(AppEvent.TraditionalSessionStarted, { ...settings });
            }
        },
    });

    const currentExercise = useMemo(() => data?.exercises[currentExerciseIndex], [data, currentExerciseIndex]);
    const numberOfExercises = data?.exercises.length ?? 0;

    useEffect(() => {
        const id = currentExercise?.id;
        if (id) {
            logWithPayload(AppEvent.ExerciseStarted, { id });
        }
        // logWithPayload should never change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentExercise]);

    const scrollToTop = () => {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth", // Optional, adds an animation
        });
    };

    useBlocker();

    const handleNextButtonClick = (skip?: boolean) => {
        if (!skip && currentExercise) {
            addExercise({
                ...currentExercise,
                duration: calculateTimePassedSeconds(startTime),
            });
            setStartTime(new Date());
        }

        if (numberOfExercises > 0) {
            setCurrentExerciseIndex((prev) => (prev + 1) % numberOfExercises);
        }

        scrollToTop();
    };

    const beforeEndButtonClick = async () => {
        if (currentExercise) {
            await finishSessionAndNavigateToSummary({
                ...currentExercise,
                duration: calculateTimePassedSeconds(startTime),
            });
        } else {
            await finishSessionAndNavigateToSummary(undefined);
        }
    };

    const handleGoBack = () => {
        navigate(pages.home);
    };

    const disableNextButton = data?.exercises.length === 0 || data?.exercises.length === 1;

    return (
        <ExerciseTemplate
            onBeforeEndButtonClick={beforeEndButtonClick}
            onNextButtonClick={handleNextButtonClick}
            disableNextButton={disableNextButton}
            loading={loading}
        >
            {loading && <Loading />}
            {!loading && !data && <Error text={t("error")} />}
            {!loading && data && data.exercises.length === 0 && (
                <Stack spacing={2}>
                    <Error text={t("no_exercises")} />{" "}
                    <Button variant="contained" onClick={handleGoBack}>
                        {t("back")}
                    </Button>
                </Stack>
            )}
            {currentExercise && <ExerciseContent exercise={currentExercise} />}
        </ExerciseTemplate>
    );
};

export default TraditionalExercise;
